import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../app/AppContext';
import { callFn, uploadFile } from '../lib/api';
import type { MediaItem } from '../lib/types';

interface Props {
  mode: 'manage' | 'pick';
  onPick?: (m: MediaItem) => void;
  /** Fired after a change that affects the profile (logo). */
  onProfileChanged?: () => void;
}

const SOURCE_LABEL: Record<string, string> = { scraped: 'From your website', menu: 'Menu photo', upload: 'Uploaded', logo: 'Logo' };

/** The store's media library — scraped images, menu photos and uploads. */
export function MediaLibrary({ mode, onPick, onProfileChanged }: Props) {
  const { toast } = useApp();
  const [media, setMedia] = useState<MediaItem[] | null>(null);
  const [logoId, setLogoId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<MediaItem | null>(null);
  const [url, setUrl] = useState('');
  const fileInput = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await callFn<{ media: MediaItem[]; logoMediaId: string | null }>('list_media');
      setMedia(r.media); setLogoId(r.logoMediaId);
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not load media.', 'err'); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        if (!f.type.startsWith('image/')) { toast(`${f.name} is not an image.`, 'err'); continue; }
        const up = await uploadFile(f);
        const r = await callFn<{ media: MediaItem }>('add_media', { url: up.url, title: f.name.replace(/\.[^.]+$/, ''), filename: up.filename, contentType: up.contentType, sizeBytes: up.sizeBytes, source: 'upload' });
        if (mode === 'pick' && onPick) { onPick(r.media); return; }
      }
      toast('Uploaded.', 'ok');
      await load();
    } catch (e) { toast(e instanceof Error ? e.message : 'Upload failed.', 'err'); }
    finally { setBusy(false); if (fileInput.current) fileInput.current.value = ''; }
  };

  const addByUrl = async () => {
    const u = url.trim();
    if (!/^https?:\/\/\S+$/i.test(u)) { toast('Enter an http(s) image URL.', 'err'); return; }
    setBusy(true);
    try {
      const r = await callFn<{ media: MediaItem }>('add_media', { url: u, source: 'upload' });
      setUrl('');
      if (mode === 'pick' && onPick) { onPick(r.media); return; }
      await load();
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not add that image.', 'err'); }
    finally { setBusy(false); }
  };

  const rename = async (m: MediaItem, title: string) => {
    try { await callFn('update_media', { id: m.id, title }); setDetail(null); await load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not rename.', 'err'); }
  };
  const remove = async (m: MediaItem) => {
    try { await callFn('delete_media', { id: m.id }); setDetail(null); toast('Removed.', 'ok'); await load(); if (m.id === logoId) onProfileChanged?.(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not delete.', 'err'); }
  };
  const setLogo = async (m: MediaItem | null) => {
    try { await callFn('set_logo', { mediaId: m ? m.id : null }); setDetail(null); toast(m ? 'Logo updated.' : 'Logo cleared.', 'ok'); await load(); onProfileChanged?.(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not set the logo.', 'err'); }
  };

  return (
    <div className="media">
      <div className="media-tools">
        <input ref={fileInput} type="file" accept="image/*" multiple={mode === 'manage'} hidden onChange={(e) => void upload(e.target.files)} />
        <button className="btn" disabled={busy} onClick={() => fileInput.current?.click()}>{busy ? 'Working…' : 'Upload photo'}</button>
        <div className="row" style={{ flex: 1, minWidth: 220 }}>
          <input className="input" placeholder="…or paste an image URL" value={url} onChange={(e) => setUrl(e.target.value)} />
          <button className="btn btn-ghost" disabled={busy || !url.trim()} onClick={() => void addByUrl()}>Add</button>
        </div>
      </div>
      {!media && <p className="muted">Loading…</p>}
      {media && media.length === 0 && <p className="muted">No images yet — upload one or paste a URL.</p>}
      <div className="media-grid">
        {media?.map((m) => (
          <button key={m.id} className={`media-tile ${m.id === logoId ? 'media-logo' : ''}`} onClick={() => (mode === 'pick' && onPick ? onPick(m) : setDetail(m))} title={m.title || m.filename || ''}>
            <img src={m.url} alt={m.title || ''} loading="lazy" />
            <span className="media-caption">{m.title || m.filename || SOURCE_LABEL[m.source] || m.source}</span>
            {m.id === logoId && <span className="tag tag-ok media-tag">Logo</span>}
          </button>
        ))}
      </div>
      {detail && mode === 'manage' && (
        <MediaDetail m={detail} isLogo={detail.id === logoId} onClose={() => setDetail(null)} onRename={rename} onDelete={remove} onSetLogo={setLogo} />
      )}
    </div>
  );
}

function MediaDetail({ m, isLogo, onClose, onRename, onDelete, onSetLogo }: { m: MediaItem; isLogo: boolean; onClose: () => void; onRename: (m: MediaItem, t: string) => Promise<void>; onDelete: (m: MediaItem) => Promise<void>; onSetLogo: (m: MediaItem | null) => Promise<void> }) {
  const [title, setTitle] = useState(m.title || '');
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <img className="sheet-img" src={m.url} alt="" />
        <div className="sheet-body">
          <span className="muted small">{SOURCE_LABEL[m.source] || m.source}{m.sizeBytes ? ` · ${Math.round(m.sizeBytes / 1024)} KB` : ''}</span>
          <label>Title<input className="input" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn-sm" disabled={title === (m.title || '')} onClick={() => void onRename(m, title.trim())}>Save title</button>
            {isLogo
              ? <button className="btn btn-sm btn-ghost" onClick={() => void onSetLogo(null)}>Clear logo</button>
              : <button className="btn btn-sm btn-ghost" onClick={() => void onSetLogo(m)}>Use as logo</button>}
            {!confirm
              ? <button className="btn btn-sm btn-danger" onClick={() => setConfirm(true)}>Delete</button>
              : <button className="btn btn-sm btn-danger" onClick={() => void onDelete(m)}>Really delete</button>}
            <button className="btn btn-sm btn-ghost" onClick={onClose} style={{ marginLeft: 'auto' }}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Modal picker used by the menu editor. */
export function MediaPicker({ onPick, onClose }: { onPick: (m: MediaItem) => void; onClose: () => void }) {
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet sheet-wide" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-body">
          <div className="row between"><h3>Choose from media library</h3><button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button></div>
          <MediaLibrary mode="pick" onPick={onPick} />
        </div>
      </div>
    </div>
  );
}
